import {
  IOsiSampleDocumentsWidgetViewModel,
  IOsiSampleDocumentsWidgetViewModelToken
} from '@shared/types/mobx/widgets/OsiSampleDocuments';
import { observer } from 'mobx-react-lite';
import { useInjection } from 'inversify-react';
import React, { useState } from 'react';
import { tokens, TranslatedToken, useTranslation } from '@shared/utils/i18n';
import { Autocomplete, Box, Button, Grid, MenuItem, TextField } from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { Abonent } from '@shared/types/osi/abonents';

export const OsiSampleDocumentsWidget: React.FC = observer(() => {
  const vm = useInjection<IOsiSampleDocumentsWidgetViewModel>(IOsiSampleDocumentsWidgetViewModelToken);

  const [selectedAbonent, setSelectedAbonent] = useState<Abonent | null>(null);
  const [selectedDoc, setSelectedDoc] = useState(-1);
  // selectedMode уходит в query-параметр mode и определяет логику расчёта долга на бэке:
  // 0 = Текущий долг, 1 = Просроченный (дефолт), 2 = Долг для взыскания. Актуально только для notary.
  const [selectedMode, setSelectedMode] = useState(1);
  const { translateToken: tt, t } = useTranslation();

  const docs = [
    { name: t('debts:debtDocs'), fn: vm.downloadDebtor },
    { name: t('debts:notaryDocs'), fn: vm.downloadNotary }
  ];

  // Признак того, что выбран именно «Заявление нотариусу» — по ссылке на метод vm.downloadNotary
  const isNotary = selectedDoc >= 0 && docs[selectedDoc]?.fn === vm.downloadNotary;

  const handleDownload = async (abonent: Abonent | null) => {
    if (!abonent) return;

    // Для notary прокидываем выбранный режим расчёта долга, для остальных — обычный вызов
    if (isNotary) {
      vm.downloadNotary(abonent, selectedMode);
    } else {
      docs[selectedDoc].fn(abonent);
    }
  };

  const handleDocChange = (event: any) => {
    setSelectedDoc(event.target.value);
  };

  const handleModeChange = (event: any) => {
    setSelectedMode(Number(event.target.value));
  };

  return (
    <Box display="flex" alignItems="center">
      <Grid container spacing={1}>
        <Grid item xs={12} sm={3}>
          <Autocomplete
            fullWidth
            disableClearable
            disabled={vm.isLoading}
            getOptionLabel={(option) => option.flat}
            renderOption={(props, option) => <li {...props}>{option.flat}</li>}
            options={vm.sortedAbonents}
            onChange={(event, value) => {
              setSelectedAbonent(value);
            }}
            // @ts-expect-error autocomplete not work with undefined
            value={selectedAbonent}
            renderInput={(params) => (
              <TextField
                {...params}
                label={tt(tokens.common.formFields.selectFlat.label)}
                size="small"
                InputProps={{
                  ...params.InputProps,
                  autoComplete: 'new-password'
                }}
                style={{ margin: 0 }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            select
            fullWidth
            defaultValue={tt(tokens.common.formFields.selectNotify.label)}
            size="small"
            value={selectedDoc}
            label={tt(tokens.common.formFields.selectNotify.label)}
            onChange={handleDocChange}
            disabled={!selectedAbonent || vm.isLoading}
          >
            {docs.map((item, i) => (
              <MenuItem key={i} value={i}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        {/* Выбор режима расчёта долга — показываем только для «Заявления нотариусу» */}
        {isNotary && (
          <Grid item xs={12} sm={3}>
            <TextField
              select
              fullWidth
              size="small"
              value={selectedMode}
              label={t('debts:notaryModeLabel')}
              onChange={handleModeChange}
              disabled={!selectedAbonent || vm.isLoading}
            >
              <MenuItem value={0}>{t('debts:notaryModeCurrent')}</MenuItem>
              <MenuItem value={1}>{t('debts:notaryModeOverdue')}</MenuItem>
              <MenuItem value={2}>{t('debts:notaryModeCollection')}</MenuItem>
            </TextField>
          </Grid>
        )}
        <Grid item xs={12} sm={3}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<CloudDownloadIcon />}
            onClick={() => handleDownload(selectedAbonent)}
            sx={{ ml: 1 }}
            size="medium"
            disabled={selectedDoc < 0 || vm.isLoading}
          >
            <TranslatedToken id={tokens.common.download} />
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
});
